import { useState, useEffect } from 'react';
import { Card, Spin } from '@bancobarigui/barihaus';
import { Input, Descriptions } from 'antd';
import { FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';

import { TransmissaoData, LaudoWizardData, WizardMode } from '@/features/laudo/types/laudo-wizard.types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { getBariLogoBase64, refreshLaudoPhotos } from '@/features/laudo/pdf/utils/imageUtils';
import { responseService } from '@/services/checklists/response.service';
import { transformLaudoWizardToLaudoData } from '@/features/laudo/utils/transformLaudoData';
import { streetViewService } from '@/services/streetview.service';
import { useReviewMode } from '../../index';

const { TextArea } = Input;

interface TransmissaoStepProps {
  data: TransmissaoData;
  inspection: InspectionRequest;
  onUpdate: (data: Partial<TransmissaoData>) => void;
  laudoData: LaudoWizardData;
  mode?: WizardMode;
}

export function TransmissaoStep({ data, inspection, onUpdate, laudoData, mode = 'create' }: TransmissaoStepProps) {
  const isReviewMode = mode === 'review';
  const { selectedCorrections } = useReviewMode();
  const [generalComments, setGeneralComments] = useState(data.generalComments || '');

  const [processingUrls, setProcessingUrls] = useState(false);

  // Agrupa correções por step para exibição organizada
  const groupedCorrections = (selectedCorrections || []).reduce((acc, correction) => {
    const stepName = correction.stepName || 'Outros';
    if (!acc[stepName]) {
      acc[stepName] = [];
    }
    acc[stepName].push(correction);
    return acc;
  }, {} as Record<string, typeof selectedCorrections>);

  // Atualiza os comentários gerais no contexto quando mudam
  useEffect(() => {
    if (isReviewMode) {
      onUpdate({ generalComments });
    }
  }, [generalComments, isReviewMode]);

  useEffect(() => {
    getBariLogoBase64().then((logo) => {
      onUpdate({ logoBase64: logo });
    }).catch(() => {
    });
  }, []);

  // No layout AVM (editAutomatic) a foto de fachada vem do Street View ou de
  // upload feito pelo usuário no step Fachada. Sincroniza com transmissao para
  // o LaudoDocument usar.
  useEffect(() => {
    if (mode !== 'editAutomatic') {
      return;
    }

    const vistoriaFoto = laudoData.vistoria?.fotoFachada;
    if (vistoriaFoto && vistoriaFoto !== data.streetViewImageBase64) {
      onUpdate({ streetViewImageBase64: vistoriaFoto });
      return;
    }

    if (vistoriaFoto || data.streetViewImageBase64) {
      return;
    }

    const firstItem = inspection.items?.[0];
    if (!firstItem?.street || !firstItem?.city) {
      return;
    }
    const street = firstItem.street;
    const number = firstItem.number || 'S/N';
    const district = firstItem.district || '';
    const city = firstItem.city;
    const state = firstItem.state || '';
    const cep = firstItem.cep || '';
    const endereco = `${street}, ${number}, ${district}, ${city}/${state}, ${cep}`;

    streetViewService
      .fetchMultipleStreetViewAngles(endereco)
      .then((images) => {
        if (images.length > 0) {
          onUpdate({ streetViewImageBase64: images[0].base64 });
        }
      })
      .catch((e) => {
        console.warn('[TransmissaoStep] Street View indisponível:', e);
      });
  }, [mode, data.streetViewImageBase64, laudoData.vistoria?.fotoFachada, inspection.items]);

  const handleGerarPDF = async () => {
    setProcessingUrls(true);
    onUpdate({ processingUrls: true });

    try {
      let checklistResponses: any[] = [];

      // Fonte 1: respostas processadas do step de Vistória (já têm question embedded)
      console.log('[PDF] processedResponses disponíveis:', laudoData.vistoria.processedResponses?.length || 0);
      if (laudoData.vistoria.processedResponses && laudoData.vistoria.processedResponses.length > 0) {
        checklistResponses = laudoData.vistoria.processedResponses;
        console.log('[PDF] Usando processedResponses do VistoriaStep:', checklistResponses.length);
      }

      // Fonte 2: buscar da API se processedResponses não disponível
      if (checklistResponses.length === 0) {
        console.log('[PDF] processedResponses vazio, tentando API...');
        const firstItem = inspection.items?.[0];
        const inspectionItemId = inspection.id;
        const checklistId = firstItem?.checklists?.[0]?.checklistId;

        if (inspectionItemId && checklistId) {
          try {
            const responses = await responseService.getResponsesByInspectionId(checklistId, inspectionItemId);
            const rawResponses = Array.isArray(responses) ? responses : [];

            // A API retorna ChecklistResponse[] com answers[] — precisamos flatten
            // Cada ChecklistResponse tem { answers: ChecklistAnswer[] }
            // Mas o mapper espera { question: {...}, answer: {...} }
            // Se o primeiro item tem .question, são processedResponses; se tem .answers, são ChecklistResponse
            if (rawResponses.length > 0 && rawResponses[0].answers) {
              // Formato ChecklistResponse — flatten answers
              for (const resp of rawResponses) {
                if (resp.answers && Array.isArray(resp.answers)) {
                  for (const ans of resp.answers) {
                    checklistResponses.push({
                      question: {
                        id: ans.questionId,
                        title: ans.questionId,
                        answerType: ans.questionAnswerType || 'ShortAnswer',
                        showInSimplifiedReport: true,
                      },
                      answer: ans,
                    });
                  }
                }
              }
            } else {
              // Já está no formato processado
              checklistResponses = rawResponses;
            }
          } catch (err) {
            console.error('[TransmissaoStep] Falha ao buscar respostas do checklist via API', {
              checklistId,
              inspectionItemId,
              error: err,
            });
            checklistResponses = [];
          }
        } else {
          console.error('[TransmissaoStep] checklistId ou inspectionItemId ausentes — questionário ficará vazio', {
            checklistId,
            inspectionItemId,
            hasFirstItem: !!firstItem,
            hasChecklists: !!firstItem?.checklists?.length,
          });
        }
      }

      // Transforma respostas da API para o formato esperado pelo PDF
      let questionarioData: any[] = [];
      if (checklistResponses.length > 0) {
        questionarioData = checklistResponses.map((response: any) => {
          const answerType = response.question?.answerType || 'Text';
          const answer = response.answer;

          // Mapeia cada tipo de resposta para o formato esperado pelo AnswerRenderer
          const showInReport = response.question?.showInSimplifiedReport ?? true;
          console.log(`[PDF] Question "${response.question?.title}" showInSimplifiedReport=${response.question?.showInSimplifiedReport} → ${showInReport}`);
          const mappedItem: any = {
            id: response.question?.id || '',
            pergunta: response.question?.title || '',
            answerType: answerType,
            isNotApplicable: false,
            showInSimplifiedReport: showInReport,
            sectionName: response.question?.sectionName || response.sectionName || undefined,
            sectionOrder: response.question?.sectionOrder ?? response.sectionOrder ?? undefined,
          };

          // Mapeia campos baseado no tipo de resposta
          switch (answerType) {
            case 'ShortAnswer':
            case 'LongAnswer':
              mappedItem.resposta = answer?.textValue || '';
              break;

            case 'YesNo':
              mappedItem.resposta = answer?.selectedOption || '';
              break;

            case 'RadioButton':
              mappedItem.resposta = answer?.selectedOption || '';
              break;

            case 'Checkbox':
              mappedItem.respostas = answer?.selectedOptions || [];
              break;

            case 'Date':
              mappedItem.data = answer?.dateValue || null;
              break;

            case 'NumericInteger':
            case 'NumericDecimal':
            case 'Currency':
              mappedItem.valor = answer?.numericValue ?? null;
              break;

            case 'Photo':
              mappedItem.photos = answer?.photos || [];
              break;

            case 'File':
              mappedItem.files = answer?.files || [];
              break;

            case 'Table':
              mappedItem.tableData = answer?.tableData || [];
              break;

            default:
              mappedItem.resposta = String(answer?.textValue || '');
          }

          if (answer?.notes) {
            mappedItem.notes = answer.notes;
          }

          return mappedItem;
        });
      } else {
        // Laudo Automático ou inspeção sem checklist preenchido
        // NÃO usar dados mockados - deixar questionário vazio
        questionarioData = [];
      }

      // Transforma dados do wizard para o formato esperado pelo PDF
      const laudoComDadosReais = transformLaudoWizardToLaudoData(
        laudoData,
        inspection,
        questionarioData
      );

      // SEMPRE processar URLs das imagens (amostras, fachada, etc.)
      // Mesmo que as fotos da vistoria já estejam processadas, as imagens das amostras precisam ser convertidas
      const updatedLaudo = await refreshLaudoPhotos(laudoComDadosReais);

      onUpdate({
        pdfGerado: true,
        processedLaudo: updatedLaudo,
        processingUrls: false,
      });

    } catch {
      // Mesmo com erro, tenta gerar PDF com dados parciais (sem usar dados mockados)
      let fallbackLaudo;
      try {
        // Usar questionário vazio em vez de dados mockados
        const questionarioData: any[] = [];
        const laudoComDadosReais = transformLaudoWizardToLaudoData(
          laudoData,
          inspection,
          questionarioData
        );
        fallbackLaudo = laudoComDadosReais;
      } catch {
        // Criar estrutura mínima sem dados mockados
        fallbackLaudo = transformLaudoWizardToLaudoData(laudoData, inspection, []);
      }
      onUpdate({
        pdfGerado: true,
        processedLaudo: fallbackLaudo,
        processingUrls: false,
      });
    } finally {
      setProcessingUrls(false);
    }
  };

  // Verifica se a foto de fachada ainda está sendo carregada
  const fotoFachadaPendente = laudoData.vistoria?.fotoFachadaDocumentId && !laudoData.vistoria?.fotoFachada;

  // Auto-gera o PDF quando o componente é montado (se ainda não foi gerado)
  useEffect(() => {
    // Aguardar foto de fachada carregar antes de gerar o PDF
    if (fotoFachadaPendente) {
      return;
    }

    // Gera PDF se não tiver processedLaudo OU se a flag pdfGerado for false
    if (!data.processedLaudo && !processingUrls) {
      handleGerarPDF();
    }
  }, [data.pdfGerado, data.processedLaudo, processingUrls, fotoFachadaPendente, laudoData.vistoria?.fotoFachada]);

  // Prepara os dados para exibição
  const item = inspection.items?.[0];
  const enderecoCompleto = laudoData.identificacao.enderecoCompleto ||
    (item ?
      `${item.street}, ${item.number}${item.complement ? `, ${item.complement}` : ''} - ${item.district}, ${item.city}/${item.state}`
      : 'N/A');

  const dataSolicitacao = inspection.createdAt
    ? new Date(inspection.createdAt).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'N/A';

  const proponenteNome = (laudoData.identificacao.proponente && laudoData.identificacao.proponente !== 'N/A')
    ? laudoData.identificacao.proponente
    : inspection.proponents?.[0]?.name || 'N/A';

  // Modo Review: Exibe resumo das correções marcadas e textarea para comentários
  if (isReviewMode) {
    const totalCorrections = selectedCorrections?.length || 0;

    return (
      <div className="transmissao-step">
        <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
          Resumo da Revisão
        </h4>

        {/* Resumo das correções marcadas */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileTextOutlined style={{ color: totalCorrections > 0 ? '#ff4d4f' : '#52c41a' }} />
              <span>
                Campos marcados para correção
              </span>
            </div>
          }
          style={{ marginBottom: 24 }}
        >
          {totalCorrections === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#52c41a' }}>
              <CheckCircleOutlined style={{ fontSize: 48, marginBottom: 12 }} />
              <p style={{ margin: 0, fontSize: 14 }}>
                O laudo será aprovado sem correções ao finalizar.
              </p>
            </div>
          ) : (
            <div>
              {Object.entries(groupedCorrections).map(([stepName, corrections]) => (
                <div key={stepName} style={{ marginBottom: 16 }}>
                  <h5 style={{ fontSize: 14, fontWeight: 600, color: '#000000', marginBottom: 8 }}>
                    {stepName}:
                  </h5>
                  <div>
                    {corrections.map((correction, idx) => (
                      <div
                        key={idx}
                        style={{
                          padding: '0px 0px',
                          borderRadius: 4,
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ fontWeight: 500, color: '#595959', fontSize: 12 }}>
                          <span style={{ color: '#595959' }}>{correction.fieldLabel}</span>
                          {correction.errorMessage && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: 2, marginLeft: 8 }}>
                              <span style={{ color: '#999', marginRight: 6 }}>↳</span>
                              <span style={{ color: '#888', fontStyle: 'italic', fontWeight: 400 }}>{correction.errorMessage}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Textarea para comentários gerais */}
        <Card title="Comentários Gerais" style={{ marginBottom: 24 }}>
          <TextArea
            value={generalComments}
            onChange={(e) => setGeneralComments(e.target.value)}
            placeholder="Adicione comentários gerais sobre a revisão do laudo"
            rows={4}
            style={{ resize: 'vertical' }}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="transmissao-step">
      <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
        {mode === 'calculations' ? 'Transmissão de Cálculos' : 'Transmissão'}
      </h4>

      {/* Indicador de laudo sem cálculos (não mostra no modo calculations pois está preenchendo) */}
      {laudoData.amostras.skipCalculations && mode !== 'calculations' && (
        <Card style={{ marginBottom: 16, border: '1px solid #faad14', background: '#fffbe6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>⚠</span>
            <div>
              <strong style={{ color: '#d48806' }}>Este laudo será transmitido sem cálculos</strong>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#8c6d1f' }}>
                O banco poderá realizar os cálculos ou encaminhá-los para outra prestadora após a revisão.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Dados Básicos do Laudo */}
      <Card title="Dados do Laudo" style={{ marginBottom: 24 }}>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Proposta">
            {inspection.proposalNumber || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Pedido">
            {inspection.id || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Dt. Solicitação">
            {dataSolicitacao}
          </Descriptions.Item>
          <Descriptions.Item label="Tipo do imóvel">
            {laudoData.avaliacao.dadosImovel.tipoImovel || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Endereço">
            {enderecoCompleto}
          </Descriptions.Item>
          <Descriptions.Item label="Proponente">
            {proponenteNome}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Estado de Processamento */}
      {processingUrls && (
        <div style={{ textAlign: 'center', padding: '20px 0', marginBottom: 24 }}>
          <Spin size="small" />
          <p style={{ fontSize: 12, color: '#1890ff', marginTop: 8 }}>
            Processando laudo...
          </p>
        </div>
      )}

      {/* Mensagem de Sucesso */}
      {data.transmissao.statusTransmissao === 'enviado' && (
        <Card style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <p style={{ marginBottom: 4, color: '#52c41a', fontWeight: 600, fontSize: 15 }}>
              ✓ Laudo transmitido com sucesso!
            </p>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>
              Enviado em {new Date(data.transmissao.dataTransmissao!).toLocaleString('pt-BR')}
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
